import React, { useEffect, useState } from 'react';

import { useButton, useI18n, useLicenses, useSelector, useStaffCan } from '~Hooks';
import { useChequeContext } from '~Scenes/Handy/Context/ChequeContext';
import PayButtons from '~Scenes/Tables/Components/PayButtons';
import { useTableContext } from '~Scenes/Handy/Context/TableContext';
import { RedButton, WhiteButton } from '~Styles';
import RemoveItemModal from '~Scenes/Tables/Components/RemoveItemModal';
import useModal from '~ComponentsImproved/Modal/useModal';
import styled from '@emotion/styled';
import DiscountButton from '~Components/DiscountButton';

const ButtonsContainer = styled.div`
  justify-self: flex-end;
  width: 100%;
  padding: 0 12px;
  box-sizing: border-box;
  margin-bottom: 8px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-bottom: 8px;

  > button,
  > div {
    min-width: calc(50% - 4px);
  }
`;

interface Props {
  canDeleteCheque: boolean;
  disableSplit: boolean;
  onSplit: () => void;
  onDeleteCheque?: () => void;
  onCancelSelection: () => void;
  selectedItemIds: string[];
  disallowRemove: boolean;
  /** Lines handed over by the quantity stepper's "Remove N" */
  pendingRemoveIds: string[];
  onClearPendingRemove: () => void;
  /** The [-] [Change] [+] row, rendered by the parent when the selection allows it */
  quantityStepper?: React.ReactNode;
}

// This type declares the different states based on which the buttons change
type OperationState = 'DEFAULT' | 'DISCOUNT' | 'SELECTION';

export default function ActionButtons({
  canDeleteCheque,
  onSplit,
  disableSplit,
  onDeleteCheque,
  onCancelSelection,
  selectedItemIds,
  disallowRemove = false,
  pendingRemoveIds,
  onClearPendingRemove,
  quantityStepper,
}: Props) {
  const { i18n } = useI18n();
  const staffCan = useStaffCan();
  const { selectedCheque } = useChequeContext();
  const { selectedTable } = useTableContext();
  const licenses = useLicenses();
  const removeModal = useModal();

  const deviceWakeState = useSelector((state) => state.deviceWakeState);

  // Represents the current operation for which this components should show buttons
  const [operationState, setOperationState] = useState<OperationState>('DEFAULT');

  // Currently we use REFUND permission
  const staffCanDelete = staffCan.REFUND;
  const staffCanMutate = staffCan.mutateCheque(selectedCheque?.id ?? '');

  const cancelSelectionClickHandlers = useButton({
    onClick: onCancelSelection,
  });

  useEffect(() => {
    if (selectedItemIds.length) {
      setOperationState('SELECTION');
    } else {
      setOperationState('DEFAULT');
    }
  }, [selectedItemIds]);

  const removeButton = useButton({
    onClick: () => removeModal.show(),
    disabled: disallowRemove || deviceWakeState.syncingTableEvents,
    disabledToast: deviceWakeState.syncingTableEvents
      ? i18n('Handy.Tables.LoadingEvents')
      : i18n('TM.CantRemoveItemsWithPayments'),
  });

  const splitButton = useButton({
    onClick: onSplit,
    disabled: disableSplit || deviceWakeState.syncingTableEvents,
    disabledToast: deviceWakeState.syncingTableEvents
      ? i18n('Handy.Tables.LoadingEvents')
      : i18n('TM.SplitPartialDisabled'),
  });

  const deleteChequeButton = useButton({
    onClick: onDeleteCheque!,
    disabled: deviceWakeState.syncingTableEvents,
    disabledToast: i18n('Handy.Tables.LoadingEvents'),
  });

  // SELECTED ITEMS, visible when the operator has selected items in the cheque
  if (operationState === 'SELECTION') {
    return (
      <ButtonsContainer>
        {staffCanMutate && staffCanDelete && (
          <>
            <RedButton {...removeButton} fluid marginBottom="xs">
              {i18n('TM.Remove')}
            </RedButton>

            {(removeModal.isShowing || pendingRemoveIds.length > 0) && (
              <RemoveItemModal
                tableId={selectedCheque?.tableId ?? ''}
                tableName={selectedTable?.code ?? ''}
                selectedItems={pendingRemoveIds.length ? pendingRemoveIds : selectedItemIds}
                clearChosen={() => {
                  onCancelSelection();
                  onClearPendingRemove();
                  removeModal.hide();
                }}
                closeModal={() => {
                  onClearPendingRemove();
                  removeModal.hide();
                }}
              />
            )}
          </>
        )}
        {quantityStepper && <ButtonRow>{quantityStepper}</ButtonRow>}
        <ButtonRow>
          {(licenses.hasHandheldPro || licenses.hasHandheldOrder) && (
            <DiscountButton
              selectedItemIds={selectedItemIds}
              onComplete={() => onCancelSelection()}
              isHandyTSR
              selectedChequeId={selectedCheque?.id}
            />
          )}
        </ButtonRow>
        <WhiteButton data-testid="cancel-selection" {...cancelSelectionClickHandlers} fluid>
          {i18n('General.Cancel')}
        </WhiteButton>
      </ButtonsContainer>
    );
  }

  if (canDeleteCheque && staffCanDelete && staffCanMutate && onDeleteCheque) {
    return (
      <ButtonsContainer>
        <RedButton data-testid="remove-cheque" {...deleteChequeButton} fluid>
          {i18n('TM.Remove')}
        </RedButton>
      </ButtonsContainer>
    );
  }

  // DEFAULT BUTTONS, These are the "default" buttons
  return (
    <ButtonsContainer>
      {staffCanMutate && (
        <>
          <ButtonRow>
            <WhiteButton data-testid="split-cheque" {...splitButton} fluid>
              {i18n('TM.Split')}
            </WhiteButton>
            {(licenses.hasHandheldPro || licenses.hasHandheldOrder) && (
              <DiscountButton
                selectedItemIds={selectedItemIds}
                onComplete={() => onCancelSelection()}
                isHandyTSR
                selectedChequeId={selectedCheque?.id}
              />
            )}
          </ButtonRow>

          <PayButtons selectedTable={selectedTable} selectedChequeId={selectedCheque?.id} />
        </>
      )}
    </ButtonsContainer>
  );
}
